import { isEmpty, isNotEmpty } from './isEmpty'
// oxlint-disable no-console
import { stringifyCircularJSON } from './error'

const levels = {
  error: { numericLevel: 0, awsLevel: 'ERROR' },
  warn: { numericLevel: 1, awsLevel: 'WARN' },
  info: { numericLevel: 2, awsLevel: 'INFO' },
  timer: { numericLevel: 3, awsLevel: 'INFO' },
  verbose: { numericLevel: 4, awsLevel: 'DEBUG' },
} as const
export type Level = keyof typeof levels

export type LogFormat = 'text' | 'json'

export type LoggerOptions = {
  level?: Level
}

const DEFAULT_LEVEL: Level = 'timer'

// SST sets SST_RESOURCE_App on every deployed Lambda. When present we let AWS handle
// level filtering via applicationLogLevel instead of filtering per-instance ourselves -
// the per-instance `level` field becomes a no-op on backend so callers don't need to
// keep it in sync with the Lambda's applicationLogLevel.
const IS_BACKEND = typeof process !== 'undefined' && !!process.env?.SST_RESOURCE_App

// CloudWatch's per-event hard limit is 1 MB. 200 KB leaves headroom for normal logs
// while preventing a single bloated record from running up the bill.
const DEFAULT_MAX_LINE_BYTES = 200 * 1024
const PREVIEW_BYTES = 512

const encoder = new TextEncoder()

type RequestIdResolver = {
  get: () => { active: boolean; requestId?: string }
  set: (requestId: string | undefined) => boolean
}

let requestIdResolver: RequestIdResolver | undefined

export function setLoggerRequestIdResolver(resolver: RequestIdResolver): void {
  requestIdResolver = resolver
}

/**
 * Logger emits structured JSON log records when format is 'json' (auto-detected when
 * `AWS_LAMBDA_LOG_FORMAT=JSON` is set on the function), otherwise plain text via
 * console.X with the logger name as a prefix.
 *
 * In JSON mode, records are written directly to stdout newline-delimited so AWS Lambda's
 * runtime passes them through unchanged. This keeps every field at the top level of the
 * CloudWatch event (no `message: { ... }` nesting), preserves our own timestamp captured
 * at log time (instead of flush/wrap time), and includes our own `requestId` set via
 * `Logger.requestId = context.awsRequestId` at handler init.
 *
 * @see https://docs.aws.amazon.com/lambda/latest/dg/nodejs-logging.html#node-js-logging-advanced
 */
export class Logger {
  public static showLoggerOutput = true
  public static format: LogFormat = detectFormat()
  // Holds the id only when no request-scoped resolver is active (code outside a request's
  // storage, or where none is registered); inside a request the resolver keeps each
  // request's id separate, so concurrent requests never share this static.
  private static fallbackRequestId: string | undefined

  public static get requestId(): string | undefined {
    const resolved = requestIdResolver?.get()
    if (resolved?.active) return resolved.requestId
    return Logger.fallbackRequestId
  }

  public static set requestId(requestId: string | undefined) {
    if (requestIdResolver?.set(requestId)) return
    Logger.fallbackRequestId = requestId
  }

  public static maxLineBytes: number = DEFAULT_MAX_LINE_BYTES

  public name: string
  public level: Level
  private startTime?: number

  constructor(name: string, opts?: LoggerOptions) {
    this.name = name
    this.level = opts?.level ?? DEFAULT_LEVEL
  }

  error(message: unknown, ...other: unknown[]): void {
    this.log('error', message, ...other)
  }

  warn(message: unknown, ...other: unknown[]): void {
    this.log('warn', message, ...other)
  }

  info(message: unknown, ...other: unknown[]): void {
    this.log('info', message, ...other)
  }

  verbose(message: unknown, ...other: unknown[]): void {
    this.log('verbose', message, ...other)
  }

  log(level: Level, message: unknown, ...other: unknown[]): void {
    if (this.shouldFilter(level)) return

    if (level === 'error') return this.write(level, message, other)

    if (level === 'warn') {
      this.write(level, message, other)
      return
    }

    if (!Logger.showLoggerOutput) return

    this.write(level, message, other)
  }

  startTimer(): void {
    this.startTime = Date.now()
  }

  timer(message: string): number {
    const elapsed = Date.now() - (this.startTime ?? 0)
    this.log('timer', `${elapsed.toString()}ms - ${message}`)
    return elapsed
  }

  // JSON mode: write the record as a newline-delimited JSON line to stdout so AWS
  // Lambda passes it through unchanged.
  // Text mode: prefix the logger name onto a string message and forward extras as
  // separate args so devtools render Errors and objects interactively.
  private write(level: Level, message: unknown, others: readonly unknown[]): void {
    if (Logger.format === 'json') {
      writeJsonLine(
        safeJsonStringify(buildJsonRecord(this.name, level, message, others), this.name, Logger.requestId),
        level
      )
      return
    }
    const consoleMethod = consoleMethodFor(level)
    if (typeof message === 'string') {
      consoleMethod(`${this.name}: ${message}`, ...others)
      return
    }
    consoleMethod(`${this.name}:`, message, ...others)
  }

  private shouldFilter(level: Level): boolean {
    if (IS_BACKEND) return false
    return levels[level].numericLevel > levels[this.level].numericLevel
  }
}

function detectFormat(): LogFormat {
  if (typeof process === 'undefined') return 'text'
  return process.env?.AWS_LAMBDA_LOG_FORMAT?.toUpperCase() === 'JSON' ? 'json' : 'text'
}

function isError(value: unknown): value is Error {
  return value instanceof Error
}

function parseStackTrace(stack: string | undefined): string[] | undefined {
  if (isEmpty(stack)) return undefined
  return stack
    .split('\n')
    .map((line) => line.trim())
    .filter(isNotEmpty)
}

// Builds the JSON record we ship to stdout in JSON mode. AWS Lambda's runtime treats
// newline-delimited JSON written to stdout as already-encoded - it does not wrap it,
// so every field we emit ends up at the TOP level in CloudWatch (no `message: { ... }`
// nesting envelope). That's how we get queryable `context.X` plus our own captured
// timestamp + requestId.
//
// Field shape:
//   timestamp   ISO 8601 / RFC 3339 - required for Lambda's level filter to work
//   level       AWS log level string (TRACE/DEBUG/INFO/WARN/ERROR/FATAL)
//   requestId   set by handler init via Logger.requestId; omitted if missing
//   logger      Logger instance name
//   message     first string arg, or first Error's message, or null
//   context     remaining non-string non-Error args (single value or array)
//   errorType / errorMessage / stackTrace   from the first Error in args
function buildJsonRecord(
  loggerName: string,
  level: Level,
  message: unknown,
  others: readonly unknown[]
): Record<string, unknown> {
  const record: Record<string, unknown> = {
    timestamp: new Date().toISOString(),
    level: levels[level].awsLevel,
  }
  if (Logger.requestId !== undefined) record.requestId = Logger.requestId
  record.logger = loggerName

  let stringMessage: string | undefined
  let firstError: Error | undefined
  const contextArgs: unknown[] = []

  for (const arg of [message, ...others]) {
    if (stringMessage === undefined && typeof arg === 'string') {
      stringMessage = arg
      continue
    }
    if (firstError === undefined && isError(arg)) {
      firstError = arg
      continue
    }
    if (arg !== undefined) {
      contextArgs.push(arg)
    }
  }

  record.message = stringMessage ?? firstError?.message ?? null

  if (firstError !== undefined) {
    record.errorType = firstError.name
    record.errorMessage = firstError.message
    record.stackTrace = parseStackTrace(firstError.stack)
  }

  if (contextArgs.length === 1) {
    record.context = contextArgs[0]
  } else if (contextArgs.length > 1) {
    record.context = contextArgs
  }

  return record
}

function safeJsonStringify(value: unknown, name: string, requestId?: string): string {
  try {
    return stringifyCircularJSON(value)
  } catch (err) {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      level: 'ERROR',
      logger: name,
      requestId,
      message: 'Logger failed to serialize record',
      errorMessage: err instanceof Error ? err.message : String(err),
    })
  }
}

// Writes a JSON line to stdout in node/Lambda; falls back to the level-appropriate
// console method in browsers (no process.stdout). The browser fallback preserves
// devtools coloring (red for errors, yellow for warns).
function writeJsonLine(line: string, level: Level): void {
  const safeLine = capLineBytes(line, level)
  if (typeof process !== 'undefined' && typeof process.stdout?.write === 'function') {
    process.stdout.write(`${safeLine}\n`)
    return
  }
  consoleMethodFor(level)(safeLine)
}

function capLineBytes(line: string, originalLevel: Level): string {
  const lineBytes = encoder.encode(line).length
  if (lineBytes <= Logger.maxLineBytes) return line
  return JSON.stringify({
    timestamp: new Date().toISOString(),
    level: 'ERROR',
    requestId: Logger.requestId,
    logger: 'logger/cap',
    message: `log line dropped: ${lineBytes.toString()} bytes exceeds ${Logger.maxLineBytes.toString()}-byte cap (original level: ${levels[originalLevel].awsLevel})`,
    preview: line.slice(0, PREVIEW_BYTES),
  })
}

function consoleMethodFor(level: Level): (...args: unknown[]) => void {
  if (level === 'error') return console.error
  if (level === 'warn') return console.warn
  if (level === 'verbose') return console.debug
  return console.log
}
